import { Component, Input, OnInit, Output, ViewChild, inject, signal } from "@angular/core";
import * as FileSaver from "file-saver";
import { Table, TableLazyLoadEvent, TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";
import { Pageable } from "../../../../modules/lipidcompass-backend-client/model/pageable";
import { paging } from "../../../environments/environment";
import { TableColumn } from "../../_models/table-column";
import { CrudAdapter } from "./crud-adapter";
import { LoggingService, LogLevel } from "../../_services/logging.service";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { MultiSelectModule } from "primeng/multiselect";
import { EllipsisPipe } from "../../_pipes/ellipsis.pipe";
import { ButtonGroupModule } from "primeng/buttongroup";
import { FormsModule } from "@angular/forms";

@Component({
    selector: "app-crud-table",
    templateUrl: "./crud-table.component.html",
    styleUrls: ["./crud-table.component.css"],
    standalone: true,
    imports: [
      ButtonModule,
      ButtonGroupModule,
      CommonModule,
      EllipsisPipe,
      FormsModule,
      InputGroupAddonModule,
      InputGroupModule,
      MultiSelectModule,
      TableModule,
      TooltipModule
    ]
})
export class CrudTableComponent implements OnInit {
  private loggingService = inject(LoggingService);

  @ViewChild("dt1") dataTable: Table | undefined;
  @Input() title: string = "";
  @Input() columns: TableColumn[] = [];
  @Input() filterFields: string[] = [];
  @Input() crudAdapter: CrudAdapter<any, any> | undefined;
  _selectedColumns: TableColumn[] = [];

  @Output() selectedItems: any[] = [];
  // Signals for the p-table's bound state. The table is [lazy]="true" and emits
  // onLazyLoad during change detection; updating these from the load callback
  // (async resolution or a re-entrant onLazyLoad) via a plain field would mutate
  // the bound value mid-CD and throw NG0100. Signals defer the view update to the
  // next change-detection cycle, so that cannot happen. (loading has no external
  // binding, so it is internal state rather than an @Input.)
  loading = signal(true);
  pagedItems = signal<any[]>([]);
  totalElements = signal(0);
  // spread operator to shallow clone
  pageable: Pageable = { ...paging.defaultPageable };
  startingRow: number = 1;

  ngOnInit(): void {
    var initialLoadEvent: TableLazyLoadEvent = {
      first: 0,
      rows: this.pageable.size,
    };
    this.loadItems(initialLoadEvent);
  }

  loadItems(event: TableLazyLoadEvent) {
    if (!this.crudAdapter) {
      this.loggingService.error("CrudTableComponent", "CrudAdapter is not set");
      return;
    }
    this.loggingService.log(
      "CrudTableComponent",
      "Received event: " + JSON.stringify(event),
      LogLevel.DEBUG
    );
    this.loading.set(true);
    this.pageable.page = (event.first ?? 0) / (event.rows ?? paging.defaultPageable.size);
    this.pageable.size = event.rows ?? paging.defaultPageable.size;
    var requestSort:string[] = [];
    if (event.sortField) {
        requestSort = [
            event.sortField + "," + (event.sortOrder == 1 ? "asc" : "desc"),
        ];
    }
    if (event.multiSortMeta) {
        requestSort = event.multiSortMeta.map((sort) => {
            return sort.field + "," + (sort.order == 1 ? "asc" : "desc");
        });
    }
    this.startingRow = (event.first ?? 0) + 1;
    this.loggingService.log(
      "CrudTableComponent",
      "Requesting data for page: " + JSON.stringify(this.pageable),
      LogLevel.DEBUG
    );
    this.crudAdapter
      .get(this.pageable.page, this.pageable.size, requestSort)
      .subscribe((pagedModel) => {
        this.loggingService.log(
          "CrudTableComponent",
          JSON.stringify(pagedModel),
          LogLevel.DEBUG
        );
        if (this.crudAdapter) {
          this.pagedItems.set(this.crudAdapter.extractEntries(pagedModel));
          this.totalElements.set(pagedModel.page.totalElements);
          if (!this._selectedColumns || this._selectedColumns.length === 0) {
            this._selectedColumns = this.columns;
          }
        } else {
          this.loggingService.error("CrudTableComponent", "CrudAdapter is not set");
        }
        this.loading.set(false);
      });
  }

  exportExcel() {
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(this.pagedItems() || []);
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, "data");
      // { Sheets: { data: worksheet }, SheetNames: ["data"] };
      xlsx.writeFileXLSX(
        workbook,
        "lipidcompass-table" + "_export_" + new Date().getTime() + ".xlsx"
      );
      // this.saveAsExcelFile(excelBuffer, "lipidcompass-table");
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    let EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    let EXCEL_EXTENSION = ".xlsx";
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(
      data,
      fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
    );
  }

  edit(rowData: any) {
    this.loggingService.debug("CrudTableComponent", "Editing rowData: " + JSON.stringify(rowData));
    if (!this.crudAdapter) {
      this.loggingService.error("CrudTableComponent", "CrudAdapter is not set");
      return;
    }
    this.crudAdapter.edit(rowData);
  }

  delete(rowData: any) {
    this.loggingService.debug("CrudTableComponent", "Deleting rowData: " + JSON.stringify(rowData));
    if (!this.crudAdapter) {
      this.loggingService.error("CrudTableComponent", "CrudAdapter is not set");
      return;
    }
    this.crudAdapter.delete(rowData);
  }

  onClick(rowData: any) {
    this.loggingService.debug("CrudTableComponent", "onClick rowData: " + JSON.stringify(rowData));
    if (!this.crudAdapter) {
      this.loggingService.error("CrudTableComponent", "CrudAdapter is not set");
      return;
    }
    this.crudAdapter.onClick(rowData);
  }

  refresh() {
    var refreshEvent: TableLazyLoadEvent = {
      first: (this.pageable.page || 0) * (this.pageable.size || paging.defaultPageable.size),
      rows: this.pageable.size || paging.defaultPageable.size,
    };
    this.loadItems(refreshEvent);
  }

  // applyFilterGlobal($event: any, stringVal: string) {
  //   this.dataTable?.filterGlobal(
  //     ($event.target as HTMLInputElement).value,
  //     stringVal
  //   );
  // }

  @Input() get selectedColumns(): TableColumn[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: TableColumn[]) {
    //restore original order
    this._selectedColumns = this.columns.filter((col) => val.includes(col));
  }
}
